import { setAuthToken } from 'features/auth/hooks/useAuth';
import { removeCookies } from 'features/auth/utils/removeCookies';

/**
 * Check for a switcher token in the URL hash fragment.
 *
 * When the Dispatch backend's switcher callback handler redirects back to the FE,
 * it appends the session JWT as:
 *   /some/path#switcher_token=<jwt>
 *
 * This function reads the token, stores it in the fp_access_token cookie
 * via setAuthToken, and clears the hash fragment so the token doesn't
 * linger in browser history, copy-pasted URLs, screenshots, or client-side
 * logs. (URL fragments are not sent in HTTP Referer headers.)
 *
 * Call once on app startup, before React mounts.
 *
 * @returns true if a switcher token was found and stored
 */
export function handleSwitcherTokenFromHash(): boolean {
  const hash = window.location.hash;
  if (!hash) return false;

  const prefix = '#switcher_token=';
  if (!hash.startsWith(prefix)) return false;

  // Take only the token value, dropping any additional hash params
  // (e.g. `#switcher_token=<jwt>&foo=bar`).
  const token = hash.slice(prefix.length).split('&')[0];
  if (!token.trim()) return false;

  // Validate it's a plausible JWT (3 dot-separated parts)
  if (token.split('.').length !== 3) return false;

  // Start a clean session for the switched-in user: clear the previous user's
  // auth + identity cookies first, so a switch-in can't inherit a stale identity
  // (cross-user / cross-tenant contamination). AuthGuard then re-fetches
  // `current` for the new token (tenant_slug is now empty).
  removeCookies();

  // Store in fp_access_token cookie (30-day expiry matches setAuthToken behavior)
  setAuthToken(token, 30);

  // Clear the hash fragment
  if (window.history?.replaceState) {
    const cleanUrl = window.location.pathname + window.location.search;
    window.history.replaceState(null, '', cleanUrl);
  } else {
    window.location.hash = '';
  }

  return true;
}
